import { Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { finalize } from 'rxjs/operators';
import { defer, forkJoin } from 'rxjs';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { api_routes, db_tables } from '@app/consts';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TablePermissionsService } from '@app/core/services/table-permissions/table-permissions.service';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { EmployeeBankDetailsService } from './services/employee-bank-details.service';
import { EmployeeBankDetails } from './models/employee-bank-details.model';

@Component({
  selector: 'app-employee-bank-details',
  templateUrl: './employee-bank-details.component.html',
  styleUrls: ['./employee-bank-details.component.scss']
})
export class EmployeeBankDetailsComponent implements OnInit {
  @Input() formIds: string[];
  employeeID: string;

  public columns: any[] = [
    { field: 'bankAccountName', title: 'bank Account Name', tableId: "tfi_BankAccountName" },
    { field: 'bankSortCode', title: 'bank Sort Code', tableId: "tfi_BankSortCode" },
    { field: 'bankAccountNumber', title: 'bank Account Number', tableId: "tfi_BankAccountNumber" },
    { field: 'buildingSocietyRollNo', title: 'building Society Roll No', tableId: "tfi_BuildingSocietyRollNo" },
    { field: 'bankName', title: 'bank Name', tableId: "tfi_BankName" },
    { field: 'iban', title: 'iban', tableId: "tfi_IBAN" },
    { field: 'swift', title: 'swift', tableId: "tfi_SWIFT" },
    { field: 'big', title: 'big', tableId: "tfi_BIG" },
    { field: 'percentageAllocated', title: 'percentage Allocated', tableId: "tfi_PercentageAllocated" },
    { field: 'comments', title: 'comments', tableId: "tfi_Comments" }
  ];

  public filterCategories: any[] = [
    { field: 'bankAccountName', title: 'Bank Account Name', dataType: 'String', tableId: "tfi_BankAccountName" },
    { field: 'bankSortCode', title: 'Bank Sort Code', dataType: 'String', tableId: "tfi_BankSortCode" },
    { field: 'bankAccountNumber', title: 'Bank Account Number', dataType: 'String', tableId: "tfi_BankAccountNumber" },
    { field: 'buildingSocietyRollNo', title: 'Building Society Roll No', dataType: 'String', tableId: "tfi_BuildingSocietyRollNo" },
    { field: 'bankName', title: 'Bank Name', dataType: 'String', tableId: "tfi_BankName" },
    { field: 'iban', title: 'IBAN', dataType: 'String', tableId: "tfi_IBAN" },
    { field: 'swift', title: 'SWIFT', dataType: 'String', tableId: "tfi_SWIFT" },
    { field: 'big', title: 'BIG', dataType: 'String', tableId: "tfi_BIG" },
    { field: 'percentageAllocated', title: 'Percentage Allocated', dataType: 'Number', tableId: "tfi_PercentageAllocated" },
    { field: 'comments', title: 'Comments', dataType: 'String', tableId: "tfi_Comments" }
  ].sort((a, b) => a.field.localeCompare(b.field));

  sortableColumns: any[] = [
    { field: "bankAccountName", sortValue: "bankAccountName" },
    { field: "bankSortCode", sortValue: "bankSortCode" },
    { field: "bankAccountNumber", sortValue: "bankAccountNumber" },
    { field: "buildingSocietyRollNo", sortValue: "buildingSocietyRollNo" },
    { field: "bankName", sortValue: "bankName" },
    { field: "iban", sortValue: "iban" },
    { field: "swift", sortValue: "swift" },
    { field: "big", sortValue: "big" },
    { field: "percentageAllocated", sortValue: "percentageAllocated" },
    { field: "comments", sortValue: "comments" }
  ];

  public bindingType: String = "array";
  public view: any;
  public gridData: any;
  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 5;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  formId: string = 'frm_hF9jQTZ6HQR3ed'
  changeReasonFormId: string = 'frm_9FKbvUllhUq91m';
  dialogRef: any;
  formValid: any;
  isLoadingPermissions: boolean = true;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  accessAndHistoryURL: string = `${api_routes.TALENT}/[employeeId]/${api_routes.BANK_DETAILS}/[entityId]`;
  filterString: string;
  sortString: string;
  getRequest: any;

  constructor(
    private dialog: MatDialog,
    private employeeBankDetailsService: EmployeeBankDetailsService,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private route: ActivatedRoute,
    private tablePermissionsService: TablePermissionsService,
    private overlayService: OverlayService
  ) {
    this.route.paramMap.subscribe(
      params => this.employeeID = params.get("id")
    )
  }

  ngOnInit(): void {
    this.view = this.getBankDetails();
    this.getTargetEmployeeTablePermissions();
  }

  getBankDetails() {
    this.isLoading = true;

    this.getRequest = this.employeeBankDetailsService.getBankDetails(this.employeeID, this.pageSize, this.skip, this.filterString, this.sortString)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: res.data,
            total: res.total,
          }
          this.gridData = res.data;
        }
      );
  }

  getBankDetail(bankDetail?: EmployeeBankDetails) {
    if (bankDetail === undefined) {
      this.openFormDialog();
    }
    else {
      this.isLoading = true;
      this.employeeBankDetailsService.getBankDetail(this.employeeID, bankDetail.id)
        .pipe(
          finalize(() => this.isLoading = false)
        )
        .subscribe(
          res => {
            this.openFormDialog(res);
          }
        );
    }
  }

  getTargetEmployeeTablePermissions() {
    this.isLoadingPermissions = true;

    let currentFetchedData = this.tablePermissionsService.getFetchedTargetEmployeeTablePermissions(this.employeeID);

    // if the current table permissions that are saved are for this employee use those, else fetch the new permissions.
    if (currentFetchedData !== null) {
      this.targetEmployeeTablePermissions = currentFetchedData;
      this.isLoadingPermissions = false;
    }
    else {
      this.tablePermissionsService.getTargetEmployeeTablePermissions(this.employeeID)
        .pipe(
          finalize(() => {
            this.isLoadingPermissions = false;
          })
        )
        .subscribe(
          res => {
            this.targetEmployeeTablePermissions = res;
          }
        );
    }
  }

  openFormDialog(employeeBankDetail?: EmployeeBankDetails) {
    let formData = {
      id: employeeBankDetail ? employeeBankDetail.id : null,
      asOf: moment().format(),
      changeReason: null,
      changeReasonComments: null,
      bankAccountName: employeeBankDetail ? employeeBankDetail.bankAccountName : null,
      bankSortCode: employeeBankDetail ? employeeBankDetail.bankSortCode : null,
      bankAccountNumber: employeeBankDetail ? employeeBankDetail.bankAccountNumber : null,
      buildingSocietyRollNo: employeeBankDetail ? employeeBankDetail.buildingSocietyRollNo : null,
      bankName: employeeBankDetail ? employeeBankDetail.bankName : null,
      iban: employeeBankDetail ? employeeBankDetail.iban : null,
      swift: employeeBankDetail ? employeeBankDetail.swift : null,
      big: employeeBankDetail ? employeeBankDetail.big : null,
      percentageAllocated: employeeBankDetail ? employeeBankDetail.percentageAllocated : null,
      comments: employeeBankDetail ? employeeBankDetail.comments : null,
    }

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formTitle: `${this.translate.instant('BankDetail')}`,
      formId: this.formId,
      changeReasonFormId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any) {
    this.overlayService.show();

    if (formData.id === null) {
      this.employeeBankDetailsService.createBankDetail(this.employeeID, formData)
        .pipe(
          finalize(() => this.overlayService.hide())
        )
        .subscribe(
          (res) => {
            this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
            this.dialogRef.close(true);
            this.getBankDetails();
          }
        );
    }
    else {
      this.employeeBankDetailsService.updateBankDetail(this.employeeID, formData)
        .pipe(
          finalize(() => this.overlayService.hide())
        )
        .subscribe(
          (res) => {
            this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
            this.dialogRef.close(true);
            this.getBankDetails();
          }
        );
    }
  }

  deleteBankDetails(BankDetailIds: string[]) {
    const observables = BankDetailIds.map(selectedItem => defer(() => this.employeeBankDetailsService.deleteBankDetail(this.employeeID, selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .pipe(
        finalize(() => this.overlayService.hide())
      )
      .subscribe(
        (res) => {
          this.view = this.getBankDetails();
          this.clearSelectedItems = !this.clearSelectedItems;
          this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getBankDetails();
  }
}